import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { PopupDialog, TextFieldComponent, CheckboxComponent, TabPanelComponent } from '../../../../components/common';
import { UnidadMedidaSelect, EmpaqueSelect } from '../../../../components/parametros';
import { MarcaSelect, ModeloSelect } from '../../marcas/components';
import { EspecieSelect, PresentacionSelect, CalibreSelect } from '../../especies/components';
import { type ArticuloEditDialogProps, type ArticuloResponse, type ArticuloRequest } from '../types/articulo';
import { useArticuloForm } from '../hooks';
import { ArticuloService } from '../services';


// Helper function to convert ArticuloResponse to ArticuloRequest
const convertToArticuloRequest = (response: ArticuloResponse): ArticuloRequest => ({
  idArticulo: response.idArticulo,
  descripcion: response.descripcion,
  codigo: response.codigo || undefined,
  marca: response.marca || undefined,
  modelo: response.modelo || undefined,
  ubicacion: response.ubicacion || undefined,
  precio: response.precio || undefined,
  idCategoria: response.idCategoria || undefined,
  idUnidadMedida: response.idUnidadMedida || undefined,
  idEstado: 1, // Default active state
  servicio: response.servicio,
});

const ARTICULO_CONSTANTS = {
  DIALOG_CONFIG: {
    DEFAULT_TITLE: 'Artículo',
    MAX_WIDTH: 'md' as const,
  },
  FIELD_LABELS: {
    descripcion: 'Descripción',
    codigo: 'Código',
    marca: 'Marca',
    modelo: 'Modelo',
    ubicacion: 'Ubicación',
    precio: 'Precio',
    idCategoria: 'Categoría',
    idUnidadMedida: 'Unidad de Medida',
    idEstado: 'Estado',
    servicio: 'Servicio',
  },
};

/**
 * EditArticulo Component
 *
 * A comprehensive articulo editing dialog that combines form fields and dialog functionality.
 */

const EditArticulo: React.FC<ArticuloEditDialogProps> = ({
  open,
  articuloIdArticulo: articuloIdArticulo,
  onClose,
  onSave,
  title = ARTICULO_CONSTANTS.DIALOG_CONFIG.DEFAULT_TITLE,
}) => {
  // Local state for categorias and articulo data
  const [, setArticuloData] = useState<ArticuloResponse | null>(null);
  const [loadingArticulo, setLoadingArticulo] = useState(false);
  const [initialArticuloRequest, setInitialArticuloRequest] = useState<ArticuloRequest | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Note: Species-related state is now handled by individual select components


  // Additional form state for new fields
  const [formState, setFormState] = useState({
    // Datos Generales
    servicio: false,
    produccion: false,
    articulo: '',
    descripcion: '',
    codigoFabrica: '',
    ubicacion: '',
    codigoBarra: '',
    marca: '',
    modelo: '',
    categoria: '',

    // Parámetros de Stock
    habilitarControlStock: false,
    permiteStockNegativo: false,
    aplicaStockMinimo: false,
    stockMinimo: 0,
    puntoPedido: 0,
    puntoMaximo: 0,

    // Producción
    idEspecie: '',
    idCalibre: '',
    idEmpaqueExterno: '',
    idPresentacion: '',
    idEmpaqueInterno: '',
    idUnidadMedida: '',
    descripcionCompleta: ''
  });

  const {
    formData,
    isValid,
    isDirty,
    isSubmitting,
    handleSubmit,
  } = useArticuloForm({
    initialArticuloRequest: initialArticuloRequest,
    isOpen: open,
    onSubmit: onSave,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFormStateChange = (field: string, value: string | number | boolean) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Note: Description generation is simplified since select components handle their own data
  const generateDescripcionCompleta = () => {
    // This would need to be enhanced to get labels from the select components
    // or maintain a simple concatenation of selected IDs for now
    const parts: string[] = [];

    // For now, we'll use a basic implementation
    // In a more sophisticated version, we could pass callbacks to get labels
    if (formState.idEspecie) parts.push(`Especie:${formState.idEspecie}`);
    if (formState.idPresentacion) parts.push(`Presentación:${formState.idPresentacion}`);
    if (formState.idCalibre) parts.push(`Calibre:${formState.idCalibre}`);

    return parts.join(' ');
  };

  // Fetch articulo data when dialog opens
  useEffect(() => {
    const fetchArticulo = async () => {
      if (!open || !articuloIdArticulo) {
        setInitialArticuloRequest(null);
        setArticuloData(null);
        return;
      }

      try {
        setLoadingArticulo(true);
        const data = await ArticuloService.getArticuloById(articuloIdArticulo);
        setArticuloData(data);
        setInitialArticuloRequest(convertToArticuloRequest(data));

        // Update formState with loaded article data
        setFormState(prev => ({
          ...prev,
          // Datos Generales
          servicio: data.servicio || false,
          articulo: data.descripcion || '',
          descripcion: data.descripcion || '',
          codigoFabrica: data.codigo || '',
          ubicacion: data.ubicacion || '',
          marca: data.marca || '',
          modelo: data.modelo || '',
          // Reset production fields if no data is available
          // These would need to be populated from the actual API response
          // when the backend provides these fields
        }));
      } catch (error) {
        console.error('Error fetching articulo:', error);
        setArticuloData(null);
        setInitialArticuloRequest(null);
      } finally {
        setLoadingArticulo(false);
      }
    };

    fetchArticulo();
  }, [open, articuloIdArticulo]);

  // Handle new articulo case (no articuloIdArticulo)
  useEffect(() => {
    if (open && !articuloIdArticulo) {
      setInitialArticuloRequest(null);
      setArticuloData(null);

      // Reset formState to defaults for new article
      setFormState({
        // Datos Generales
        servicio: false,
        produccion: false,
        articulo: '',
        descripcion: '',
        codigoFabrica: '',
        ubicacion: '',
        codigoBarra: '',
        marca: '',
        modelo: '',
        categoria: '',

        // Parámetros de Stock
        habilitarControlStock: false,
        permiteStockNegativo: false,
        aplicaStockMinimo: false,
        stockMinimo: 0,
        puntoPedido: 0,
        puntoMaximo: 0,

        // Producción
        idEspecie: '',
        idCalibre: '',
        idEmpaqueExterno: '',
        idPresentacion: '',
        idEmpaqueInterno: '',
        idUnidadMedida: '',
        descripcionCompleta: ''
      });
    }
  }, [open, articuloIdArticulo]);

  // Reset tab when dialog opens
  useEffect(() => {
    if (open) {
      setTabValue(0);
    }
  }, [open]);




  // Note: Data fetching is now handled by individual select components
  // Handle clearing dependent fields when parent selections change
  const handleEspecieChange = (value: string | number) => {
    handleFormStateChange('idEspecie', value);
    // Clear dependent fields
    handleFormStateChange('idPresentacion', '');
    handleFormStateChange('idCalibre', '');
  };

  const handlePresentacionChange = (value: string | number) => {
    handleFormStateChange('idPresentacion', value);
    // Clear dependent field
    handleFormStateChange('idCalibre', '');
  };



  // Auto-generate descripcionCompleta when production fields change
  useEffect(() => {
    const newDescripcionCompleta = generateDescripcionCompleta();
    if (newDescripcionCompleta !== formState.descripcionCompleta) {
      setFormState(prev => ({
        ...prev,
        descripcionCompleta: newDescripcionCompleta
      }));
    }
  }, [
    formState.idEspecie,
    formState.idPresentacion,
    formState.idCalibre,
    formState.idEmpaqueInterno,
    formState.idEmpaqueExterno,
    formState.idUnidadMedida
  ]);



  // Note: Options are now handled by individual select components


  const handleCancelClick = (): void => {
    if (isDirty) {
      // In a real application, you might want to show a confirmation dialog
      // before discarding changes
    }
    onClose();
  };

  // Early return if no form data or still loading
  if (!formData || (articuloIdArticulo && loadingArticulo)) {
    return null;
  }

  const dialogTitle = `${title}${isDirty ? ' *' : ''}`;

  return (
      <PopupDialog
        open={open}
        onClose={handleCancelClick}
        title={dialogTitle}
        maxWidth={ARTICULO_CONSTANTS.DIALOG_CONFIG.MAX_WIDTH}
        disableEscapeKeyDown={isDirty}
        primaryAction={{
          label: 'Guardar',
          onClick: handleSubmit,
          disabled: !isValid,
          loading: isSubmitting,
        }}
        secondaryAction={{
          label: 'Cancelar',
          onClick: handleCancelClick,
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Datos Generales" />
              <Tab label="Parámetros de Stock" />
              <Tab label="Producción" />
            </Tabs>
          </Box>

          <TabPanelComponent value={tabValue} index={0}>
            {/* Datos Generales */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <CheckboxComponent
                label="Servicio"
                checked={formState.servicio}
                onChange={(checked) => handleFormStateChange('servicio', checked)}
              />
              <CheckboxComponent
                label="Producción"
                checked={formState.produccion}
                onChange={(checked) => handleFormStateChange('produccion', checked)}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextFieldComponent
                label="Descripción"
                value={formState.descripcion}
                onChange={(value) => handleFormStateChange('descripcion', value)}
                fullWidth
                required
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextFieldComponent
                label="Código Fábrica"
                value={formState.codigoFabrica}
                onChange={(value) => handleFormStateChange('codigoFabrica', value)}
                fullWidth
              />
              <TextFieldComponent
                label="Ubicación"
                value={formState.ubicacion}
                onChange={(value) => handleFormStateChange('ubicacion', value)}
                fullWidth
              />
              <TextFieldComponent
                label="Código Barra"
                value={formState.codigoBarra}
                onChange={(value) => handleFormStateChange('codigoBarra', value)}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <MarcaSelect
                value={formState.marca}
                onChange={(value: string | number) => handleFormStateChange('marca', value)}
                label="Marca"
              />
              <ModeloSelect
                value={formState.modelo}
                onChange={(value: string | number) => handleFormStateChange('modelo', value)}
                idMarca={formState.marca}
                label="Modelo"
              />
            </Box>

          </TabPanelComponent>

          <TabPanelComponent value={tabValue} index={1}>
            {/* Parámetros de Stock */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <CheckboxComponent
                label="Habilitar Control de Stock"
                checked={formState.habilitarControlStock}
                onChange={(checked) => handleFormStateChange('habilitarControlStock', checked)}
              />
              <CheckboxComponent
                label="Permite Stock Negativo"
                checked={formState.permiteStockNegativo}
                onChange={(checked) => handleFormStateChange('permiteStockNegativo', checked)}
              />
              <CheckboxComponent
                label="Aplica Stock Mínimo"
                checked={formState.aplicaStockMinimo}
                onChange={(checked) => handleFormStateChange('aplicaStockMinimo', checked)}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextFieldComponent
                label="Stock Mínimo"
                type="number"
                value={formState.stockMinimo}
                onChange={(value) => handleFormStateChange('stockMinimo', Number(value))}
                fullWidth
              />
              <TextFieldComponent
                label="Punto de Pedido"
                type="number"
                value={formState.puntoPedido}
                onChange={(value) => handleFormStateChange('puntoPedido', Number(value))}
                fullWidth
              />
              <TextFieldComponent
                label="Punto Máximo"
                type="number"
                value={formState.puntoMaximo}
                onChange={(value) => handleFormStateChange('puntoMaximo', Number(value))}
                fullWidth
              />
            </Box>
          </TabPanelComponent>

          <TabPanelComponent value={tabValue} index={2}>
            {/* Producción */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <EspecieSelect
                value={formState.idEspecie}
                onChange={handleEspecieChange}
                label="Especie"
              />
              <PresentacionSelect
                value={formState.idPresentacion}
                onChange={handlePresentacionChange}
                idEspecie={formState.idEspecie}
                label="Presentación"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
              <CalibreSelect
                value={formState.idCalibre}
                onChange={(value: string | number) => handleFormStateChange('idCalibre', value)}
                idPresentacion={formState.idPresentacion}
                label="Calibre"
              />
              <EmpaqueSelect
                label="Empaque Interno"
                value={formState.idEmpaqueInterno}
                onChange={(value: string | number) => handleFormStateChange('idEmpaqueInterno', value)}
                isExterno={false}
              />
              <EmpaqueSelect
                label="Empaque Externo"
                value={formState.idEmpaqueExterno}
                onChange={(value: string | number) => handleFormStateChange('idEmpaqueExterno', value)}
                isExterno={true}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <UnidadMedidaSelect
                value={formState.idUnidadMedida}
                onChange={(value: string | number) => handleFormStateChange('idUnidadMedida', value)}
                label="Unidad de Medida"
              />
            </Box>

            <TextFieldComponent
              label="Descripción Completa"
              value={formState.descripcionCompleta}
              onChange={(value) => handleFormStateChange('descripcionCompleta', value)}
              multiline
              rows={4}
              fullWidth
            />
          </TabPanelComponent>
        </Box>
      </PopupDialog>
  );
};

export default EditArticulo;